/**
 * Respostas rápidas (pedido da PoolTrack, 02/10) — leituras e escritas.
 *
 * - a lista da configuração: GET /chat/quick-replies (todas, ativas e inativas);
 * - a do compositor: GET /chats/:id/quick-replies — já filtrada pela mesa da
 *   conversa e com o valor de cada variável ali.
 *
 * Fora do CHATS_KEY de propósito: invalidar a lista de conversas não refaz as
 * respostas. Escrever na configuração invalida as duas.
 */
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ChatQuickReplies,
  QuickReply,
  QuickReplyCreateBody,
  QuickReplyUpdateBody,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { errorMessage as chatErrorMessage } from './chats-format';

export const quickRepliesKey = ['erp', 'chat-quick-replies'] as const;

export function quickRepliesOptions() {
  return queryOptions({
    queryKey: [...quickRepliesKey, 'all'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<QuickReply[]>('/chat/quick-replies', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/**
 * As do compositor de UMA conversa. 30 s de folga: uma resposta nova do admin
 * aparece no próximo foco da conversa; o nome da pessoa ou o ticket vinculado
 * mudando no meio entram no mesmo intervalo.
 */
export function chatQuickRepliesOptions(chatId: number) {
  return queryOptions({
    queryKey: [...quickRepliesKey, 'chat', chatId] as const,
    queryFn: async ({ signal }) => {
      const res = await api<ChatQuickReplies>(`/chats/${chatId}/quick-replies`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 30_000,
  });
}

const ERROR_LABELS: Record<string, string> = {
  quick_reply_shortcut_exists: 'Já existe uma resposta com esse atalho.',
  quick_reply_not_found: 'Resposta não encontrada — recarregue a lista.',
  desk_not_found: 'Mesa não encontrada — recarregue a página.',
  invalid_body: 'Confira os campos: título, atalho e texto.',
  forbidden: 'Só administradores alteram as respostas rápidas.',
};

export function quickReplyErrorMessage(e: unknown): string {
  const code =
    e !== null && typeof e === 'object' && 'error' in e ? (e as { error: unknown }).error : null;
  if (typeof code === 'string' && ERROR_LABELS[code]) return ERROR_LABELS[code];
  return chatErrorMessage(e);
}

export function useSaveQuickReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: { id: null; body: QuickReplyCreateBody } | { id: number; body: QuickReplyUpdateBody }
    ) => {
      const res =
        input.id === null
          ? await api<QuickReply>('/chat/quick-replies', { method: 'POST', body: input.body })
          : await api<QuickReply>(`/chat/quick-replies/${input.id}`, {
              method: 'PUT',
              body: input.body,
            });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: quickRepliesKey }),
  });
}

export function useDeleteQuickReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api<void>(`/chat/quick-replies/${id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: quickRepliesKey }),
  });
}
